export const LIST_INDENT_SPACES = 2;

export type ListMarkerKind = 'bullet' | 'number' | 'none';

export type ListFormatAction = 'bullet' | 'number' | 'indent' | 'outdent';

export type ParsedListLine = {
  indentLevel: number;
  markerKind: ListMarkerKind;
  markerNumber?: number;
  body: string;
};

const BULLET_RE = /^(\s*)(?:•|[-*])\s+(.*)$/;
const NUMBER_RE = /^(\s*)(\d+)\.\s+(.*)$/;

export function parseListLine(line: string): ParsedListLine {
  const bullet = line.match(BULLET_RE);
  if (bullet) {
    return {
      indentLevel: indentLevelFromSpaces(bullet[1]),
      markerKind: 'bullet',
      body: bullet[2],
    };
  }

  const numbered = line.match(NUMBER_RE);
  if (numbered) {
    return {
      indentLevel: indentLevelFromSpaces(numbered[1]),
      markerKind: 'number',
      markerNumber: Number(numbered[2]),
      body: numbered[3],
    };
  }

  const plain = line.match(/^(\s*)(.*)$/);
  return {
    indentLevel: indentLevelFromSpaces(plain?.[1] ?? ''),
    markerKind: 'none',
    body: plain?.[2] ?? line,
  };
}

function indentLevelFromSpaces(spaces: string): number {
  return Math.floor(spaces.length / LIST_INDENT_SPACES);
}

function indentSpaces(level: number): string {
  return ' '.repeat(Math.max(0, level) * LIST_INDENT_SPACES);
}

export function formatListLine(parsed: ParsedListLine): string {
  const prefix = indentSpaces(parsed.indentLevel);
  if (parsed.markerKind === 'bullet') {
    return `${prefix}• ${parsed.body}`;
  }
  if (parsed.markerKind === 'number') {
    return `${prefix}${parsed.markerNumber ?? 1}. ${parsed.body}`;
  }
  return `${prefix}${parsed.body}`;
}

export function getAffectedLineRange(
  text: string,
  selectionStart: number,
  selectionEnd: number,
): { startLine: number; endLine: number; lineStartOffset: number; lineEndOffset: number } {
  const safeStart = Math.max(0, Math.min(selectionStart, text.length));
  const safeEnd = Math.max(safeStart, Math.min(selectionEnd, text.length));

  const lineStartOffset = text.lastIndexOf('\n', safeStart - 1) + 1;
  const nextNewline = text.indexOf('\n', safeEnd > 0 ? safeEnd - 1 : 0);
  const lineEndOffset = nextNewline === -1 ? text.length : nextNewline;

  const startLine = text.slice(0, lineStartOffset).split('\n').length - 1;
  const endLine = text.slice(0, lineEndOffset).split('\n').length - 1;

  return { startLine, endLine, lineStartOffset, lineEndOffset };
}

function renumberNumberedLines(lines: ParsedListLine[], startLine: number, endLine: number) {
  const counters: number[] = [];

  for (let i = startLine; i <= endLine; i++) {
    const line = lines[i];
    if (line.markerKind !== 'number') continue;

    const level = line.indentLevel;
    while (counters.length <= level) counters.push(0);
    while (counters.length > level + 1) counters.pop();

    counters[level] += 1;
    for (let deeper = level + 1; deeper < counters.length; deeper++) counters[deeper] = 0;
    line.markerNumber = counters[level];
  }
}

function allSelectedHaveMarker(lines: ParsedListLine[], startLine: number, endLine: number, kind: ListMarkerKind) {
  for (let i = startLine; i <= endLine; i++) {
    if (lines[i].markerKind !== kind) return false;
  }
  return true;
}

export function getListFormatState(
  text: string,
  selectionStart?: number,
  selectionEnd?: number,
): { bulletActive: boolean; numberActive: boolean } {
  const safeText = String(text ?? '');
  if (!safeText) {
    return { bulletActive: false, numberActive: false };
  }

  const start = selectionStart ?? 0;
  const end = selectionEnd ?? safeText.length;
  const { startLine, endLine } = getAffectedLineRange(safeText, start, end);
  const parsed = safeText.split('\n').map(parseListLine);

  return {
    bulletActive: allSelectedHaveMarker(parsed, startLine, endLine, 'bullet'),
    numberActive: allSelectedHaveMarker(parsed, startLine, endLine, 'number'),
  };
}

export function applyListFormatToText(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  action: ListFormatAction,
): { text: string; selectionStart: number; selectionEnd: number } {
  const { startLine, endLine, lineStartOffset, lineEndOffset } = getAffectedLineRange(
    text,
    selectionStart,
    selectionEnd,
  );

  const rawLines = text.split('\n');
  const parsed = rawLines.map(parseListLine);

  if (action === 'bullet') {
    const remove = allSelectedHaveMarker(parsed, startLine, endLine, 'bullet');
    for (let i = startLine; i <= endLine; i++) {
      if (remove) {
        parsed[i].markerKind = 'none';
        parsed[i].markerNumber = undefined;
      } else {
        parsed[i].markerKind = 'bullet';
        parsed[i].markerNumber = undefined;
      }
    }
  } else if (action === 'number') {
    const remove = allSelectedHaveMarker(parsed, startLine, endLine, 'number');
    for (let i = startLine; i <= endLine; i++) {
      if (remove) {
        parsed[i].markerKind = 'none';
        parsed[i].markerNumber = undefined;
      } else {
        parsed[i].markerKind = 'number';
      }
    }
    if (!remove) {
      renumberNumberedLines(parsed, startLine, endLine);
    }
  } else if (action === 'indent') {
    for (let i = startLine; i <= endLine; i++) {
      parsed[i].indentLevel += 1;
    }
    renumberNumberedLines(parsed, startLine, endLine);
  } else if (action === 'outdent') {
    for (let i = startLine; i <= endLine; i++) {
      parsed[i].indentLevel = Math.max(0, parsed[i].indentLevel - 1);
    }
    renumberNumberedLines(parsed, startLine, endLine);
  }

  const nextLines = parsed.map(formatListLine);
  const nextText = nextLines.join('\n');
  const nextSelectionStart = lineStartOffset;
  const nextSelectionEnd = lineStartOffset + nextLines.slice(startLine, endLine + 1).join('\n').length;

  return {
    text: nextText,
    selectionStart: nextSelectionStart,
    selectionEnd: nextSelectionEnd,
  };
}
